import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, inject } from '@angular/core';
import { UrlVerificationService } from '../services/url-verification.service';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-url-verification',
  templateUrl: './url-verification.component.html',
  styleUrls: ['./url-verification.component.scss'],
  imports: [FormsModule]
})
export class UrlVerificationComponent implements OnInit, OnDestroy {
  private urlVerificationService = inject(UrlVerificationService);

  @Input() hasAccess: boolean | null = null;
  @Input() noAccessMessage: string = '';
  @Input() assetBaseUrl: string = 'assets/';
  @Input() tenantApiBaseUrl: string = '';
  @Input() language?: string;

  @Output() successfulVerificationEvent = new EventEmitter<void>();
  @Output() mustSignOutEvent = new EventEmitter<void>();

  contentURL: string = '';
  invalidUrl: boolean = false;

  private destroy$ = new Subject<void>();

  private translations = {
    en: {
      urlVerTitle: 'URL Verification',
      urlVerInstruction:
        'Please enter the Etrieve Content URL you will be uploading your attachments to.',
      invalidUrl: 'Invalid URL',
      helpText: 'Not sure what to enter here?',
      helpTextBullet1: 'Sign into Content and copy the URL from your web browser menu bar.',
      helpTextBullet2: 'Check that your URL starts with https',
      verifyUrl: 'Verify URL',
      enterUrl: 'Enter your Etrieve Content URL'
    },
    es: {
      urlVerTitle: 'Verificación de URL',
      urlVerInstruction: 'Ingrese la URL de Etrieve Content donde cargará sus archivos adjuntos.',
      invalidUrl: 'URL inválida',
      helpText: '¿No está seguro de qué ingresar aquí?',
      helpTextBullet1:
        'Inicie sesión en Content y copie la URL de la barra de menú de su navegador web.',
      helpTextBullet2: 'Verifique que su URL comience con https',
      verifyUrl: 'Verificar URL',
      enterUrl: 'Ingrese su URL de Etrieve Content'
    }
  };

  /**
   *
   */
  get currentTranslations() {
    if (this.language && this.translations[this.language as keyof typeof this.translations]) {
      return this.translations[this.language as keyof typeof this.translations];
    }

    const browserLang = navigator.language.split('-')[0];
    return this.translations[browserLang as keyof typeof this.translations] || this.translations.en;
  }

  /**
   * Initializes the component by retrieving the stored URL from localStorage.
   */
  ngOnInit() {
    const storedUrl = localStorage.getItem('contentURL');
    if (storedUrl) {
      this.contentURL = storedUrl || '';
      this.invalidUrl = this.hasAccess === false;
    }
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verifies the new URL entered by the user.
   */
  verifyUrl(): void {
    const url = this.contentURL.toLocaleLowerCase();
    const vanityName = url.includes('/content')
      ? url.split('https://')[1]?.split('.')[0]
      : url.split('https://')[1]?.split('content')[0];

    if (vanityName && this.tenantApiBaseUrl) {
      this.urlVerificationService
        .verifyUrl(vanityName, this.tenantApiBaseUrl)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            localStorage.setItem('tenantId', response.tenantID);
            localStorage.setItem('vanityName', vanityName);
            if (
              localStorage.getItem('contentURL') &&
              this.contentURL !== localStorage.getItem('contentURL')
            ) {
              localStorage.setItem('contentURL', this.contentURL);
              this.mustSignOutEvent.emit();
            } else {
              localStorage.setItem('contentURL', this.contentURL);
              this.successfulVerificationEvent.emit();
            }
            this.invalidUrl = false;
          },
          error: () => {
            this.invalidUrl = true;
          }
        });
    } else {
      this.invalidUrl = true;
    }
  }
}
